import { getWindowObject } from '@eventbrite/feature-detection';
import { CaseOptions, flattenObject } from '@eventbrite/transformation-utils';
import { DATALAYER_PUSH_EVENT, SEND_TO_HEAP_KEY } from './constants';

/**
 * It sends data to the dataLayer to be processed by GTM/Heap
 * following the definition given by the Data team
 * @param {string} event - name of the dataLayer event
 * @param {object} data - event data to send along
 */
const push = (event: String, data: Object): void => {
    const dataLayer = getWindowObject('dataLayer');
    if (!dataLayer?.push || typeof dataLayer.push !== 'function') {
        if (process.env.NODE_ENV === 'development') {
            console.error(
                "@eventbrite/datalayer-library push error. Either dataLayer doesn't exist, or it doesn't have a push function it can call",
                { arguments: { event, data }, dataLayer },
            );
        }
        return;
    }

    dataLayer.push({
        event,
        [SEND_TO_HEAP_KEY]: true,
        ...data,
    });
};

/**
 * It sends data to the dataLayer so that page properties are updated.
 * @param {Object} data
 * @param {Object} data.page - page event data to send to dataLayer
 * @param {string} [data.eventName] - name of the tracked event, if set, event will be tracked as well
 */
export const updatePageProps = (data: {
    page: Object;
    eventName?: string;
}): void => {
    data.page = flattenObject(data.page, { case: CaseOptions.dot });
    push(DATALAYER_PUSH_EVENT.updatePageProps, data);
};

/**
 * It sends data to the dataLayer to track custom properties.
 * @param {Object} data
 * @param {string} data.eventName - name of the tracked event
 * @param {Object} data.eventData - event data to send to dataLayer
 */
export const track = (data: { eventName: String; eventData: Object }): void => {
    push(DATALAYER_PUSH_EVENT.track, data);
};
